import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, ArrowRight, BellRing, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEmployerBrand, BrandLogo, PoweredByBadge } from "@/lib/brand";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ACTION_META = {
  nudge_all: {
    icon: BellRing,
    label: "Nudge all stale candidates",
    cta: "Send reminder emails",
    color: "violet",
    body: "We'll fire a polite reminder email to every candidate in this batch. Already-engaged interviews and rows that hit your max-reminders ceiling are skipped automatically.",
  },
  nudge: {
    icon: BellRing,
    label: "Nudge candidate",
    cta: "Send reminder email",
    color: "violet",
    body: "We'll fire a polite reminder email to this candidate. If they already started or hit your ceiling, we'll skip safely.",
  },
  move_not_interested_all: {
    icon: UserX,
    label: "Move all to Not Interested",
    cta: "Move to Not Interested",
    color: "slate",
    body: "Every candidate in this batch will be moved to the Not Interested stage. This is reversible from the Applicants page.",
  },
  move_not_interested: {
    icon: UserX,
    label: "Move to Not Interested",
    cta: "Move to Not Interested",
    color: "slate",
    body: "This candidate will be moved to the Not Interested stage. Reversible from the Applicants page.",
  },
};

export default function DigestAction() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const { brand } = useEmployerBrand(info?.employer_id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/public/digest-action/${token}`
        );
        if (!cancelled) setInfo(data);
      } catch (e) {
        if (!cancelled)
          setError(
            e?.response?.data?.detail ||
              "We couldn't verify this link. It may have expired or already been used."
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/api/public/digest-action/${token}/confirm`
      );
      setResult(data);
    } catch (e) {
      setError(
        e?.response?.data?.detail || "Action failed. Please try from your Applicants page."
      );
    } finally {
      setConfirming(false);
    }
  };

  const meta = info ? ACTION_META[info.action] : null;
  const Icon = meta?.icon;

  return (
    <main
      data-testid="digest-action-page"
      className="min-h-screen bg-slate-50 px-6 py-12"
    >
      <div className="mx-auto max-w-xl rounded-sm border border-slate-200 bg-white p-8 shadow-sm">
        {brand?.logo_url && (
          <div className="mb-6 flex justify-center">
            <BrandLogo brand={brand} className="h-12 w-auto max-w-[180px] object-contain" />
          </div>
        )}
        {loading && (
          <div className="flex items-center gap-3 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            Verifying link…
          </div>
        )}

        {error && !loading && (
          <div data-testid="digest-action-error">
            <div className="mb-3 flex items-center gap-3 text-amber-700">
              <AlertTriangle className="h-6 w-6" />
              <h1 className="text-xl font-bold">Link unavailable</h1>
            </div>
            <p className="text-sm text-slate-600">{error}</p>
            <Link
              to="/applicants"
              className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-900"
            >
              Open Applicants <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {info && !result && !error && (
          <div data-testid="digest-action-confirm">
            <div
              className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-sm bg-${meta.color}-100 text-${meta.color}-700`}
            >
              {Icon && <Icon className="h-5 w-5" />}
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              {meta.label}
            </h1>
            <p
              data-testid="digest-action-count"
              className="mt-1 text-sm text-slate-500"
            >
              {info.count} candidate{info.count === 1 ? "" : "s"} in this batch.
            </p>
            <p className="mt-4 text-sm text-slate-700">{meta.body}</p>

            {info.preview?.length > 0 && (
              <ul
                data-testid="digest-action-preview"
                className="mt-4 max-h-48 space-y-1 overflow-y-auto rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs"
              >
                {info.preview.map((c, i) => (
                  <li key={i} className="flex justify-between">
                    <span className="font-medium text-slate-800">{c.name || c.email}</span>
                    <span className="font-mono text-slate-500">{c.ref}</span>
                  </li>
                ))}
                {info.count > info.preview.length && (
                  <li className="text-center text-slate-400">
                    + {info.count - info.preview.length} more…
                  </li>
                )}
              </ul>
            )}

            <div className="mt-6 flex items-center gap-3">
              <Button
                data-testid="digest-action-confirm-btn"
                onClick={confirm}
                disabled={confirming}
                className={
                  meta.color === "violet"
                    ? "bg-violet-600 text-white hover:bg-violet-500"
                    : "bg-slate-800 text-white hover:bg-slate-700"
                }
              >
                {confirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {meta.cta}
              </Button>
              <Link
                to="/applicants"
                className="text-sm text-slate-500 hover:text-slate-800"
              >
                Cancel
              </Link>
            </div>
          </div>
        )}

        {result && (
          <div data-testid="digest-action-result">
            <div className="mb-3 flex items-center gap-3 text-emerald-700">
              <CheckCircle2 className="h-6 w-6" />
              <h1 className="text-xl font-bold">Done</h1>
            </div>
            {result.nudged !== undefined && (
              <p className="text-sm text-slate-700">
                <span className="font-semibold text-violet-700">{result.nudged}</span> reminder email
                {result.nudged === 1 ? "" : "s"} sent.{" "}
                {result.skipped > 0 &&
                  `${result.skipped} skipped (engagement / ceiling).`}
              </p>
            )}
            {result.updated !== undefined && (
              <p className="text-sm text-slate-700">
                <span className="font-semibold text-slate-800">{result.updated}</span> candidate
                {result.updated === 1 ? "" : "s"} moved to <b>Not Interested</b>. You can
                undo from the Applicants page any time.
              </p>
            )}
            <Link
              to="/applicants"
              className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-900"
            >
              Open Applicants <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}
      </div>
      <div className="mx-auto mt-6 flex max-w-xl justify-end">
        <PoweredByBadge brand={brand} />
      </div>
    </main>
  );
}
